import { useTranslation } from "react-i18next"

import { useFormatPersonName } from "@/formatters"
import type { User } from "@/models/user"
import { getAdminConfirmDeleteDialogDefaults } from "@/modules/admin/utils/get-admin-confirm-delete-dialog-defaults"
import { useDeleteUser } from "@/services/user"
import { useConfirm } from "material-ui-confirm"

interface ConfirmDeleteUserTarget {
  firstName?: User["firstName"]
  id: string
  lastName?: User["lastName"]
}

interface UseConfirmDeleteUserOptions {
  onSuccess?: (userId: string) => void
}

export const useConfirmDeleteUser = ({
  onSuccess,
}: UseConfirmDeleteUserOptions = {}) => {
  const { t } = useTranslation("common")
  const confirm = useConfirm()
  const { formatPersonName } = useFormatPersonName()
  const { deleteUserIsPending, deleteUserMutateAsync } = useDeleteUser()

  const confirmAndDeleteUser = async (
    user: ConfirmDeleteUserTarget
  ): Promise<void> => {
    const { confirmed } = await confirm({
      title: t("deleteUser.confirmTitle"),
      description: t("deleteUser.confirmMessage", {
        userName: formatPersonName({
          firstName: user.firstName,
          lastName: user.lastName,
        }),
      }),
      ...getAdminConfirmDeleteDialogDefaults(t),
    })

    if (!confirmed) {
      return
    }

    await deleteUserMutateAsync({ userId: user.id })
    onSuccess?.(user.id)
  }

  return {
    confirmAndDeleteUser,
    deleteUserIsPending,
  }
}
